import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  TextField,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { fromBmarkenDateToDateTime, priceText } from "../utils/utils";
import { useBmapi } from "../utils/bmapi-context";

const cellStyle = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

function errorToText(e) {
  if (!e) return "Errore sconosciuto";
  if (typeof e === "string") return e;

  const msg =
    e?.response?.data?.message ||
    e?.response?.data?.error ||
    e?.response?.data ||
    e?.message;

  if (typeof msg === "string") return msg;

  try {
    return JSON.stringify(e, null, 2);
  } catch {
    return String(e);
  }
}

export default function UserInspectionTransactionsDialog({
  open,
  onClose,
  transactions,
  canReverseTransaction = false,
  onReversed, // callback: chiamata dopo reverse ok
}) {
  const { bmapi, startLoading, stopLoading } = useBmapi();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [txToReverse, setTxToReverse] = useState(null);

  const [reason, setReason] = useState("");

  const [errorOpen, setErrorOpen] = useState(false);
  const [errorText, setErrorText] = useState("");

  const canConfirmReverse = useMemo(() => {
    return reason.trim().length > 0;
  }, [reason]);

  const openConfirm = (tx) => {
    setTxToReverse(tx);
    setReason("");
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setTxToReverse(null);
    setReason("");
  };

  const openError = (e) => {
    setErrorText(errorToText(e));
    setErrorOpen(true);
  };

  const closeError = () => {
    setErrorOpen(false);
    setErrorText("");
  };

  const handleReverse = () => {
    if (!txToReverse?.id) return;
    const trimmed = reason.trim();
    if (!trimmed) return;

    startLoading();
    return bmapi
      .reverseTransaction(txToReverse.id, trimmed)
      .then(() => {
        closeConfirm();
        onReversed?.();
      })
      .catch((e) => {
        closeConfirm();
        openError(e);
      })
      .finally(stopLoading);
  };

  return (
    <>
      <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
        <DialogTitle>Transazioni</DialogTitle>

        <DialogContent>
          <Card>
            {transactions?.length ? (
              <CardContent>
                <div style={{ width: "100%" }}>
                  {/* Header (tabella-like) */}
                  <Box
                    display="flex"
                    alignItems="center"
                    style={{
                      padding: "8px 16px",
                      borderBottom: "1px solid rgba(0,0,0,0.12)",
                    }}
                  >
                    <Box flex={2} pr={2}>
                      <Typography variant="h6">Data</Typography>
                    </Box>
                    <Box flex={2} pr={2}>
                      <Typography variant="h6">Tipo</Typography>
                    </Box>
                    <Box flex={4} pr={2}>
                      <Typography variant="h6">Business</Typography>
                    </Box>
                    <Box flex={2} pr={2} style={{ textAlign: "right" }}>
                      <Typography variant="h6">Valore</Typography>
                    </Box>
                    <Box width={40} />
                  </Box>

                  {transactions.map((k, i) => {
                    if (!k) return null;

                    const data = fromBmarkenDateToDateTime(k.created_at);
                    const tipo = k.type ?? "-";
                    const business = k.business_name ?? "-";
                    const valore = priceText(k.value);

                    return (
                      <Accordion key={k.id || i} square>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box display="flex" alignItems="center" width="100%">
                            <Box flex={2} pr={2} style={cellStyle}>
                              <Typography>{data}</Typography>
                            </Box>
                            <Box flex={2} pr={2} style={cellStyle}>
                              <Typography>{tipo}</Typography>
                            </Box>
                            <Box flex={4} pr={2} style={cellStyle}>
                              <Typography>{business}</Typography>
                            </Box>
                            <Box
                              flex={2}
                              pr={2}
                              style={{ ...cellStyle, textAlign: "right" }}
                            >
                              <Typography>{valore}</Typography>
                            </Box>
                          </Box>
                        </AccordionSummary>

                        <AccordionDetails style={{ display: "block" }}>
                          <Box pl={4} pr={1} width="100%">
                            <table style={{ width: "100%" }}>
                              <tbody>
                                <tr>
                                  <td>
                                    <Typography variant="h6">Data:</Typography>
                                    {k.created_at}
                                  </td>
                                  <td>
                                    <Typography variant="h6">Tipo:</Typography>
                                    {k.type}
                                  </td>
                                </tr>
                                <tr>
                                  <td>
                                    <Typography variant="h6">
                                      Business ID:
                                    </Typography>
                                    {k.business_id}
                                  </td>
                                  <td>
                                    <Typography variant="h6">
                                      Business Name:
                                    </Typography>
                                    {k.business_name}
                                  </td>
                                </tr>
                                <tr>
                                  <td>
                                    <Typography variant="h6">Stato:</Typography>
                                    {k.status}
                                  </td>
                                  <td>
                                    <Typography variant="h6">
                                      Valore:
                                    </Typography>
                                    {k.value}
                                  </td>
                                </tr>
                                <tr>
                                  <td>
                                    <Typography variant="h6">
                                      Valore spesa:
                                    </Typography>
                                    {k.value_from_expense}
                                  </td>
                                  <td>
                                    <Typography variant="h6">
                                      Valore extra:
                                    </Typography>
                                    {k.value_from_extra}
                                  </td>
                                </tr>
                                <tr>
                                  <td>
                                    <Typography variant="h6">
                                      Emittente:
                                    </Typography>
                                    {k.status}
                                  </td>
                                  <td>
                                    <Typography variant="h6">
                                      Tipo ricevente:
                                    </Typography>
                                    {k.available_shots}
                                  </td>
                                </tr>
                                <tr>
                                  <td>
                                    <Typography variant="h6">
                                      Ricevente:
                                    </Typography>
                                    {k.status}
                                  </td>
                                  <td>
                                    <Typography variant="h6">
                                      Tipo ricevente:
                                    </Typography>
                                    {k.available_shots}
                                  </td>
                                </tr>
                                <tr>
                                  <td>
                                    <Typography variant="h6">
                                      ID operazione:
                                    </Typography>
                                    {k.operation_id}
                                  </td>
                                  <td>
                                    <Typography variant="h6">
                                      Valore operazione:
                                    </Typography>
                                    {k.operation_value}
                                  </td>
                                </tr>
                                <tr>
                                  <td colSpan={2}>
                                    <Typography variant="h6">
                                      Descrizione:
                                    </Typography>
                                    {k.description}
                                  </td>
                                </tr>
                              </tbody>
                            </table>

                            {canReverseTransaction ? (
                              <Box
                                mt={2}
                                display="flex"
                                justifyContent="flex-end"
                              >
                                <Button
                                  variant="contained"
                                  color="secondary"
                                  onClick={() => openConfirm(k)}
                                >
                                  Storna
                                </Button>
                              </Box>
                            ) : null}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </div>
              </CardContent>
            ) : null}
          </Card>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* Popup conferma storno */}
      <Dialog open={confirmOpen} onClose={closeConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>Conferma</DialogTitle>
        <DialogContent>
          <Typography style={{ marginBottom: 12 }}>
            Sei sicuro di voler stornare la transazione?
          </Typography>

          <TextField
            label="Motivo"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            autoFocus
            name="reason"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm}>Annulla</Button>
          <Button
            onClick={handleReverse}
            variant="contained"
            color="secondary"
            disabled={!canConfirmReverse}
          >
            Storna
          </Button>
        </DialogActions>
      </Dialog>

      {/* Popup errore */}
      <Dialog open={errorOpen} onClose={closeError} maxWidth="sm" fullWidth>
        <DialogTitle>Errore</DialogTitle>
        <DialogContent>
          <Typography style={{ whiteSpace: "pre-wrap" }}>
            {"Si è verificato un errore\n" + errorText}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeError} color="primary">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
